import { useMemo, useState } from 'react';
import { useCombobox } from 'downshift';
import type { Region } from '@/api/regions';
import { filterRegions } from '@/lib/filterRegions';

interface CountrySelectProps {
  id: string;
  regions: readonly Region[];
  selectedCode: string | null;
  onChange: (code: string) => void;
  disabled?: boolean;
  /** 利用者が文字を打ったときに呼ばれる (選択前でも「操作した」と判断するため) */
  onInteract?: () => void;
}

const itemToString = (item: Region | null) => item?.name ?? '';

/**
 * 居住地のサジェスト付きプルダウン。キーボード操作と WAI-ARIA combobox の属性は
 * downshift に任せ、見た目だけを自前で持つ。
 *
 * リストから選んだ値だけを受け付ける。自由入力を許すと、マスタに無い値が送信されうるため。
 */
export function CountrySelect({
  id,
  regions,
  selectedCode,
  onChange,
  disabled = false,
  onInteract,
}: CountrySelectProps) {
  // 入力欄の表示文字列とは別に「利用者が打った絞り込み文字列」を持つ。
  // 選択後に開き直したとき、表示中の国名で絞り込まれて 1 件しか出なくなるのを防ぐため。
  const [filterText, setFilterText] = useState('');

  const selectedItem = useMemo(
    () => regions.find((r) => r.code === selectedCode) ?? null,
    [regions, selectedCode],
  );
  const items = useMemo(() => filterRegions(regions, filterText), [regions, filterText]);

  const { isOpen, highlightedIndex, getLabelProps, getInputProps, getMenuProps, getItemProps } =
    useCombobox<Region>({
      id,
      items,
      selectedItem,
      itemToString,
      onSelectedItemChange: ({ selectedItem: item }) => {
        if (item) {
          onChange(item.code);
        }
      },
      onInputValueChange: ({ type, inputValue }) => {
        if (type === useCombobox.stateChangeTypes.InputChange) {
          setFilterText(inputValue ?? '');
          onInteract?.();
        }
      },
      onIsOpenChange: ({ isOpen: open }) => {
        if (!open) {
          setFilterText('');
        }
      },
      stateReducer: (_state, { type, changes }) => {
        // フォーカスが外れたら、打ちかけの文字を捨てて選択中の国名に戻す
        if (type === useCombobox.stateChangeTypes.InputBlur) {
          return { ...changes, inputValue: itemToString(changes.selectedItem ?? null) };
        }
        return changes;
      },
    });

  return (
    <div className="email-input country-select">
      <label {...getLabelProps()}>Country/Region of Residence</label>
      <div className="country-select-control">
        <input
          {...getInputProps({
            placeholder: 'Please select',
            disabled,
            // 開いたときに全選択しておき、打ち始めると国名が置き換わるようにする
            onFocus: (e) => e.currentTarget.select(),
          })}
        />
        <span className="country-select-arrow" aria-hidden="true" />
      </div>
      <ul {...getMenuProps()} className="country-select-menu" hidden={!isOpen || items.length === 0}>
        {isOpen &&
          items.map((item, index) => (
            <li
              key={item.code}
              className={
                highlightedIndex === index
                  ? 'country-select-option is-highlighted'
                  : 'country-select-option'
              }
              {...getItemProps({ item, index })}
            >
              {item.name}
            </li>
          ))}
      </ul>
      {isOpen && items.length === 0 && (
        <p className="country-select-empty" role="status">
          No results
        </p>
      )}
    </div>
  );
}
